import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";

const STORAGE_KEY = "mydawa.askai.log.v1";

export default function AskAI() {
  const [open, setOpen] = useState(false);
  const [log, setLog] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]"); }
    catch (e) { return []; }
  });
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(log.slice(-40))); }
    catch (e) { /* ignore */ }
    if (endRef.current) endRef.current.scrollTop = endRef.current.scrollHeight;
  }, [log, open]);

  const send = async () => {
    const q = input.trim();
    if (!q || busy) return;
    const nextLog = [...log, { role: "user", text: q, at: Date.now() }];
    setLog(nextLog);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/admin-chat", { text: q, session_id: "admin" });
      setLog((L) => [...L, { role: "assistant", text: data.reply, at: Date.now() }]);
    } catch (e) {
      setLog((L) => [...L, { role: "assistant", text: "Sorry — the AI service is unavailable right now.", at: Date.now() }]);
    } finally { setBusy(false); }
  };

  const clear = () => { setLog([]); localStorage.removeItem(STORAGE_KEY); };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-30 flex items-center gap-2 rounded-full bg-[#ff3399] px-5 py-3 text-sm font-bold text-white shadow-lg transition hover:scale-105"
          data-testid="askai-open"
        >
          <Sparkles className="h-4 w-4" /> Ask anything
        </button>
      )}

      {open && (
        <div className="fixed bottom-6 right-6 z-30 flex h-[540px] w-[380px] flex-col overflow-hidden rounded-2xl border border-[#e2e8e2] bg-white shadow-2xl" data-testid="askai-panel">
          <div className="flex items-center justify-between bg-gradient-to-r from-[#66cc33] to-[#ff3399] px-4 py-3 text-white">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              <div className="text-sm font-bold">Ask MYDAWA AI</div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={clear} className="text-[10px] font-semibold opacity-80 hover:opacity-100" data-testid="askai-clear">clear</button>
              <button onClick={() => setOpen(false)} className="opacity-80 hover:opacity-100" data-testid="askai-close">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div ref={endRef} className="flex-1 space-y-2 overflow-y-auto bg-[#fafcfa] p-3 text-sm">
            {log.length === 0 && (
              <div className="rounded-xl bg-white p-4 text-xs text-[#5c665a]">
                I have access to your Program Config and live metrics. Try:
                <ul className="mt-2 list-disc pl-4 font-semibold">
                  <li>Which referrer drove the most acquired customers last month?</li>
                  <li>Is our K-factor healthy?</li>
                  <li>What's our LTV:CAC ratio and how can we improve it?</li>
                  <li>Any budget risk today?</li>
                </ul>
              </div>
            )}
            {log.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`askai-msg-${m.role}`}>
                <div className={`max-w-[82%] whitespace-pre-wrap rounded-2xl px-3 py-2 text-[13px] ${m.role === "user" ? "bg-[#66cc33] text-white" : "bg-white border border-[#e2e8e2] text-[#1a1c19]"}`}>
                  {m.text}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="rounded-2xl border border-[#e2e8e2] bg-white px-3 py-2"><Loader2 className="h-4 w-4 animate-spin text-[#5c665a]" /></div>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 border-t border-[#e2e8e2] p-2">
            <input
              className="admin-input flex-1 text-sm"
              placeholder="Ask about metrics, referrers, payouts…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") send(); }}
              disabled={busy}
              data-testid="askai-input"
            />
            <button className="admin-btn" onClick={send} disabled={busy || !input.trim()} data-testid="askai-send">
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// Export MessageCircle so we don't trigger unused-import lints elsewhere.
export { MessageCircle };
